<script setup lang="ts">
    import {
        updateTableLayout,
        useGetInteractions,
        useGetInteractionTable,
    } from '@/js/hooks/interaction';
    import { IRowCell, ISortDirection } from '@/js/services/api/interaction/types';
    import { Column, DataTable, DataTableColumnReorderEvent, PageState } from 'primevue';
    import { computed, ref, watch } from 'vue';
    import ExpandedRow from './ExpandedRow.vue';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { __ } from '@/js/stores/expressions';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import {
        useNumericQueryParam,
        useQueryParam,
        useQueryString,
    } from '@/js/composables/useQueryString';
    import { useTablePageSize } from '@/js/composables/useTablePageSize';
    import { storeToRefs } from 'pinia';
    import TablePaginator from '../../dataTable/TablePaginator.vue';
    import TableLoadingOverlay from '../../dataTable/TableLoadingOverlay.vue';
    import FirstColumnHeader from '../../dataTable/FirstColumnHeader.vue';
    import FirstColumnBody from '../../dataTable/FirstColumnBody.vue';
    import TableToolbar from '../../dataTable/TableToolbar.vue';
    import { dateToString } from '@/js/utils/dateUtils';
    import ColumnBody from '../../dataTable/ColumnBody.vue';
    import {
        InteractionTableStoreName,
        useInteractionTableStoreByName,
    } from '@/js/stores/interactions-table';
    import ColumnHeader from '../../dataTable/ColumnHeader.vue';
    import ToggleColumnModal from '../../dataTable/ToggleColumnModal.vue';
    import FrozenColumnFade from '../../dataTable/FrozenColumnFade.vue';
    import BottomScrollFade from '../../dataTable/BottomScrollFade.vue';
    import SummaryRow from './SummaryRow.vue';
    import { getInteractionCount } from '@/js/services/api/interaction/interactionAPI.ts';
    import { useQuery } from '@tanstack/vue-query';
    import { QK } from '@/js/consts/query-keys.ts';

    const interactionsTableStore = useInteractionTableStoreByName(
        InteractionTableStoreName.InteractionsTable
    );
    const filterStore = useFilterStoreByName(FilterStoreName.SharedFilter);

    const { requestData } = storeToRefs(filterStore);

    const delayedRequestDataTimeout = ref<number | undefined>();
    const delayedRequestData = ref(requestData.value);

    watch(
        requestData,
        (newRequestData, oldRequestData) => {
            if (
                oldRequestData &&
                JSON.stringify(newRequestData) === JSON.stringify(oldRequestData)
            ) {
                return;
            }

            clearTimeout(delayedRequestDataTimeout.value);

            delayedRequestDataTimeout.value = window.setTimeout(() => {
                delayedRequestData.value = newRequestData;
                if (oldRequestData) {
                    page.value = 0;
                }
            }, 1000);
        },
        { immediate: true }
    );

    const querystring = useQueryString();

    const page = useNumericQueryParam('page', {
        startIndex: 0,
        oneBased: true,
        updateMode: 'push',
    });
    const pageSize = useTablePageSize('interactions');
    const { value: sortFieldId } = useQueryParam('field', {
        defaultValue: '',
        updateMode: 'replace',
    });
    const { value: sortOrder } = useQueryParam('order', {
        defaultValue: '',
        updateMode: 'replace',
    });

    const showColumnModal = ref(false);

    const tableRef = ref<HTMLElement | null>(null);

    const { data: interactions, isFetching } = useGetInteractionTable(
        page,
        pageSize,
        sortFieldId,
        sortOrder,
        delayedRequestData
    );

    const countQueryKey = computed(() => {
        return [QK.INTERACTIONS.COUNT, delayedRequestData.value];
    });

    const count = useQuery({
        queryKey: countQueryKey,
        queryFn: () => getInteractionCount(delayedRequestData.value),
        placeholderData: 0,
    });

    const summaryIds = computed(() =>
        interactionsTableStore.showSummary
            ? (interactions.value?.rowCells ?? []).map((row) => row.id)
            : []
    );

    const { data: summaryInteractions } = useGetInteractions(summaryIds);

    const summaryMap = computed(() => {
        const items = summaryInteractions.value?.interactions ?? [];
        return Object.fromEntries(items.map((item) => [item.id, item]));
    });

    const updateTableLayoutMutation = updateTableLayout();

    const paginationChange = async (e: PageState) => {
        const newPage = e.rows == pageSize.value ? e.page + 1 : 1;

        await querystring.setMany({ page: newPage, rows: e.rows }, 'push');
    };

    const onCustomSort = async (fieldId: string, order: 'asc' | 'desc') => {
        await querystring.setMany({ field: fieldId, order: order }, 'replace');
    };

    watch(interactions, (newData) => {
        if (newData) {
            const rowIdList = newData.rowCells.map((row) => row.id);
            interactionsTableStore.setRowsIdList(rowIdList);
            interactionsTableStore.reconcileTableColumns(newData.columnHeaders);

            // Hide main-category from the column list — it's merged into the sub-category column
            const mainCat = interactionsTableStore.tableColumns?.find(
                (c) => (c.translationKey as string) === 'table-interaction-column-main-category'
            );
            if (mainCat) mainCat.order = null;
        }
    });

    const getRowValue = (translationKey: string) =>
        translationKey.split('table-interaction-column-')[1] as keyof IRowCell;

    const tableKey = ref(0);

    const persistTableLayout = () => {
        const request = interactionsTableStore.tableColumns?.map((col) => ({
            id: col.id,
            order: col.order,
        }));
        if (request) updateTableLayoutMutation.mutate(request);
    };

    const onColumnReorder = (event: DataTableColumnReorderEvent) => {
        interactionsTableStore.onReorderTableColumn(event);
        tableKey.value++;
        persistTableLayout();
    };

    const totalRows = computed(() => {
        if (!count.isFetching.value && count.data.value !== undefined) {
            return count.data.value;
        }
        return interactions.value?.pagination.totalRows || 0;
    });
</script>
<template>
    <div
        ref="tableRef"
        class="echo-table-wrapper echo-table-wrapper--fill echo-table-wrapper--clickable-rows"
    >
        <TableToolbar
            :table-store-name="InteractionTableStoreName.InteractionsTable"
            :container-ref="tableRef"
            @show-column-modal="showColumnModal = true"
        />
        <DataTable
            :key="tableKey"
            v-model:expanded-rows="interactionsTableStore.expandedRows"
            data-key="id"
            :lazy="true"
            :rows="pageSize"
            :page="page"
            :first="page * pageSize + 1"
            :reorderable-columns="true"
            :value="interactions?.rowCells ?? []"
            :total-records="totalRows"
            :loading="isFetching || !filterStore.all"
            :paginator="true"
            :resizable-columns="false"
            :scrollable="true"
            scroll-height="flex"
            @column-reorder="onColumnReorder"
            @row-click="
                (e) => {
                    if (interactionsTableStore.showSummary) {
                        interactionsTableStore.toggleRowSummaryExpansion(e.data.id);
                    } else {
                        interactionsTableStore.toggleRowExpansion(e.data.id);
                    }
                }
            "
        >
            <template #loadingicon>
                <TableLoadingOverlay />
            </template>
            <template #empty>
                <div class="h-100 flex items-center justify-center">
                    <h2 v-if="!isFetching" class="text-center">
                        {{ __('interaction-table-empty-message') }}
                    </h2>
                </div>
            </template>
            <Column
                field="externalId"
                :column-key="'externalId'"
                :row-reorder="false"
                :reorderable-column="false"
                :frozen="true"
                class="p-column-first w-79.25 min-w-79.25 max-w-79.25"
            >
                <template #header>
                    <FirstColumnHeader
                        :table-store-name="InteractionTableStoreName.InteractionsTable"
                        :total-records="totalRows"
                        @show-column-modal="showColumnModal = true"
                    />
                </template>
                <template #body="{ data }">
                    <FirstColumnBody
                        :row-id="data.id"
                        :title="data.externalId"
                        :subtitle="`${data.agentName}, ${dateToString(data.timestamp)}`"
                        :channel-icon="data.channelIcon"
                        :checked="interactionsTableStore.selectedRows[data.id] || false"
                        :table-store-name="InteractionTableStoreName.InteractionsTable"
                    />
                </template>
            </Column>

            <Column
                v-for="item in interactionsTableStore.visibleColumns"
                :key="item.id"
                :field="item.id"
                :column-key="item.id"
            >
                <template #header>
                    <ColumnHeader
                        :sort-order="sortOrder as ISortDirection"
                        :sort-field-id="sortFieldId || undefined"
                        :header="item"
                        :filter-store-name="FilterStoreName.SharedFilter"
                        @sort="onCustomSort"
                        @hide-column="
                            () => {
                                interactionsTableStore.hideColumn(item.id);
                                persistTableLayout();
                            }
                        "
                    />
                </template>
                <template #body="{ data }">
                    <div
                        v-if="item.translationKey === 'table-interaction-column-sub-category'"
                        class="w-full h-full flex flex-col justify-center items-start text-start"
                    >
                        <p v-if="data['main-category']" class="text-caption-bold min-w-24">
                            {{ data['main-category'] }}
                        </p>
                        <p v-if="data['sub-category']" class="text-caption">
                            {{ data['sub-category'] }}
                        </p>
                    </div>
                    <ColumnBody
                        v-else-if="item.translationKey !== 'table-interaction-column-date-time'"
                        :field="getRowValue(item.translationKey)"
                        :data="data[getRowValue(item.translationKey)]"
                    />
                    <ColumnBody v-else field="date-time" :data="dateToString(data.timestamp)" />
                </template>
            </Column>
            <template #paginatorcontainer="slotProps">
                <TablePaginator
                    :total-records="slotProps.totalRecords ?? 0"
                    :rows="pageSize"
                    :first="slotProps.first"
                    :page="page"
                    :page-count="slotProps.pageCount ?? 0"
                    @pagination-change="paginationChange"
                />
            </template>
            <template #expansion="{ data }">
                <SummaryRow
                    v-if="
                        interactionsTableStore.showSummary &&
                        !interactionsTableStore.expandedInSummaryMode[data.id]
                    "
                    :interaction="summaryMap[data.id]"
                    :container-ref="tableRef"
                />
                <ExpandedRow v-else :data="data" :container-ref="tableRef" />
            </template>
        </DataTable>
        <FrozenColumnFade :container-ref="tableRef" />
        <BottomScrollFade :container-ref="tableRef" />
    </div>
    <ToggleColumnModal
        :store-name="InteractionTableStoreName.InteractionsTable"
        :show-column-modal="showColumnModal"
        @close="showColumnModal = false"
        @submit="persistTableLayout"
    />
</template>

<!-- @config-column="configTableLayout" -->
